/* eslint-disable @typescript-eslint/no-explicit-any */
import Api from '@sendsprint/api-types';
import { PaymentMethodsI } from '@src/components/paymentDropdownList/components/getPaymentMethods';
import {
  UserAxiosConfig,
  RequestConfig,
  addTokenHeader,
  BusinessAxiosConfig
} from '@src/config/AxiosConfig';
import { BUSINESS_API_VERSION } from '@src/constants';
import ClientApi from '@src/types/client';
import { clearState, getState, setState } from '@src/utils/stateStorage';
import { getFullName } from '@src/utils/user';
import { AxiosResponse } from 'axios';
import {
  decorateWithAppIdAndKey,
  decorateWithAppIdAndKeyAndEmailAndHash,
  getApiEndpointResponse
} from './api-service';

const getLoggedInDataFromApiResponse = (
  responseData: ClientApi.Account.Login.Response
): ClientApi.General.LoggedInUser => {
  const {
    data: { user: userData, token: tokenData, adminLoginResponse }
  } = responseData;

  const user: ClientApi.General.LoggedInUser['user'] = {
    email: userData.emailAddress,
    firstName: userData.firstName,
    lastName: userData.lastName,
    fullName: getFullName({ firstName: userData.firstName, lastName: userData.lastName }),
    isEmailVerified: false,
    referralCode: userData.referralCode,
    userOnboardingStatus: userData.userOnboardingStatus as 1 | 2,
    profilePixUrl: userData.profilePixUrl,
    userCategory: userData?.userCategory,
    userCategoryType: userData?.userCategoryType,
    dateOfBirth: userData?.dateOfBirth || ''
  };

  const business: ClientApi.General.LoggedInUser['business'] = {
    businessLogoUrl: userData.businessLogoUrl,
    businessName: userData.businessName,
    merchantId: userData.merchantId
  };

  const address: ClientApi.General.LoggedInUser['address'] = {
    city: userData.city,
    state: userData.state,
    country: userData.country,
    phone: userData.phoneNumber,
    postCode: userData.postCode,
    street: userData.street,
    percentage: userData.percentage || '',
    role: userData.personalRole || ''
  };

  const tokens: ClientApi.General.LoggedInUser['tokens'] = {
    ...tokenData,
    hash: userData.hash
  };

  const adminTokens: ClientApi.General.LoggedInUser['adminTokens'] = {
    SessionToken: adminLoginResponse?.sessionToken || '',
    SessionDuration: 5,
    SessionExpiryTime: ''
  };

  return { user, address, tokens, business, adminTokens };
};

// function used to save logged in user in session storage
export function persistLoggedInState(loggedInData: ClientApi.General.LoggedInUser): void {
  const { tokens, user, address, business, adminTokens } = loggedInData;
  setState({ tokens, user, address, business, adminTokens });
}

/**
 * ===================
 * ENDPOINTS
 * ===================
 */

export const loginService = async (
  payload: ClientApi.Account.Login.Request
): Promise<ClientApi.General.LoggedInUser> => {
  const config = addTokenHeader('');

  return BusinessAxiosConfig.post<
    ClientApi.Account.Login.Response,
    AxiosResponse<ClientApi.Account.Login.Response>,
    ClientApi.Account.Login.Request
  >(`/api/v${BUSINESS_API_VERSION}/Authentication/Login`, payload, config)
    .then(getApiEndpointResponse)
    .then(getLoggedInDataFromApiResponse);
};

export const loginUserService = async (
  payload: ClientApi.Account.LoginUser.Request,
  config?: RequestConfig<Api.Account.Login.Request>
): Promise<ClientApi.Account.LoginUser.Response> => {
  const serializedData: Api.Account.Login.Request = decorateWithAppIdAndKey({
    Email: payload.Email,
    Password: payload.Password,
    LoginType: ''
  });

  return UserAxiosConfig.post<
    ClientApi.Account.LoginUser.Response,
    AxiosResponse<ClientApi.Account.LoginUser.Response>,
    Api.Account.Login.Request
  >('/account/login', serializedData, config).then((res) => res.data);
};

export const registerService = async (
  payload: ClientApi.Account.Register.Request,
  config?: RequestConfig<Api.Account.Register.Request>
): Promise<[Api.Model.User]> => {
  const serializedData: Api.Account.Register.Request = decorateWithAppIdAndKey({
    FirstName: payload.FirstName,
    LastName: payload.LastName,
    Email: payload.Email,
    Password: payload.Password,
    Referrer: payload.Referrer,
    LoginType: '',
    SignUpSourceId: `${payload.SignUpSourceId}`,
    /**
     * This differentiate business from individual sign ups
     */
    UserType: 'Business'
  });

  return UserAxiosConfig.post<
    Api.Account.Register.Response,
    AxiosResponse<Api.Account.Register.Response>,
    Api.Account.Register.Request
  >('/account/register', serializedData, config)
    .then(getApiEndpointResponse)
    .then((data) => data.Data);
};

export const submitUserCategoryService = async (
  payload: ClientApi.Account.SubmitUserCategory.Request,
  config?: RequestConfig
): Promise<boolean> => {
  const token = getState()?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.post<
    ClientApi.Account.SubmitUserCategory.Response,
    AxiosResponse<ClientApi.Account.SubmitUserCategory.Response>,
    ClientApi.Account.SubmitUserCategory.Request
  >(`/api/v${BUSINESS_API_VERSION}/Onboarding/SubmitUserCategory`, payload, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const refreshTokenService = async (
  payload: ClientApi.Account.RefreshToken.Request,
  config?: RequestConfig
): Promise<ClientApi.General.TokenDetails> => {
  const token = getState()?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.put<
    ClientApi.Account.RefreshToken.Response,
    AxiosResponse<ClientApi.Account.RefreshToken.Response>,
    ClientApi.Account.RefreshToken.Request
  >(`/api/v${BUSINESS_API_VERSION}/Authentication/RefreshToken`, payload, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const logoutService = async (): Promise<boolean> => {
  const token = getState()?.tokens?.token?.accessToken;
  const sessionToken = getState()?.adminTokens?.SessionToken;
  const config = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.Account.Logout.Response,
    AxiosResponse<ClientApi.Account.Logout.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/Authentication/Logout?adminSessionToken=${sessionToken}`,
    {},
    config
  )
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const clientLogoutService = () => {
  clearState();
  return Promise.resolve({});
};

export const getSignUpSourceService = (
  config?: RequestConfig
): Promise<ClientApi.Account.SignUpSource.SignUpSourceData[]> => {
  return UserAxiosConfig.get<
    ClientApi.Account.SignUpSource.Response,
    AxiosResponse<ClientApi.Account.SignUpSource.Response>
  >('/account/loadsignupsources', config)
    .then(getApiEndpointResponse)
    .then((data) => data.Data);
};

export const generateOtpService = async (
  payload: ClientApi.Account.GenerateOtp.Request,
  config?: RequestConfig<ClientApi.Account.GenerateOtp.Request>
) => {
  const serializedData = decorateWithAppIdAndKey({
    Email: payload.email,
    Hash: payload.hash,
    Phone: payload.phone,
    Purpose: payload?.purpose || 0
  });

  return UserAxiosConfig.post<
    Api.Endpoint.Response.SuccessResponse,
    AxiosResponse<Api.Endpoint.Response.SuccessResponse>
  >('/account/generateotp', serializedData, config);
};

export const verifyOtpService = async (
  payload: ClientApi.Account.VerifyOtp.Request,
  config?: RequestConfig<ClientApi.Account.VerifyOtp.Request>
) => {
  const serializedData = decorateWithAppIdAndKey({
    Email: payload.email,
    Hash: payload.hash,
    Phone: payload.phone,
    OtpCode: payload.otp,
    Purpose: payload?.purpose || 0
  });

  return UserAxiosConfig.post<
    Api.Endpoint.Response.SuccessResponse,
    AxiosResponse<Api.Endpoint.Response.SuccessResponse>
  >('/account/verifyotp', serializedData, config);
};

export const setAddressService = async (
  payload: ClientApi.Account.SetAddress.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: RequestConfig<any>
): Promise<ClientApi.General.UserAddress> => {
  const serializedData: Api.Account.SetAddress.Request = decorateWithAppIdAndKeyAndEmailAndHash({
    Street: payload.streetAddress,
    PostalCode: payload.postCode,
    City: payload.city,
    Country: payload.country,
    Phone: payload.phone,
    PersonalRole: payload.role,
    Percentage: payload.percentage
  });

  const serializedData2: Api.Account.SetAddress.Request = decorateWithAppIdAndKeyAndEmailAndHash({
    Street: payload.streetAddress,
    PostalCode: payload.postCode,
    City: payload.city,
    Country: payload.country,
    Phone: payload.phone,
    Hash: payload.hash,
    Email: payload.email,
    State: payload.state,
    DateOfBirth: payload.dateOfBirth,
    SSN: payload.ssn,
    PersonalRole: payload.role,
    Percentage: payload.percentage
  });

  const selectedSerializedData = payload.hash ? serializedData2 : serializedData;

  return UserAxiosConfig.post<
    Api.Account.SetAddress.Response,
    AxiosResponse<Api.Account.SetAddress.Response>
  >('/account/setaddress', selectedSerializedData, config)
    .then(getApiEndpointResponse)
    .then(() => {
      // todo We should (ideally) persist address data from the backend
      const address: ClientApi.General.UserAddress = {
        city: payload.city,
        state: payload.state,
        country: payload.country,
        street: payload.streetAddress,
        postCode: payload.postCode,
        phone: payload.phone,
        percentage: payload.percentage,
        role: payload.role
      };
      setState({ address });

      return address;
    });
};

export const resetPasswordByEmailService = async (
  payload: ClientApi.Account.ResetPasswordByEmail.Request,
  config?: RequestConfig<Api.Account.ResetPasswordByEmail.Request>
): Promise<Api.Account.ResetPasswordByEmail.Response> => {
  const serializedData: Api.Account.ResetPasswordByEmail.Request = decorateWithAppIdAndKey({
    Email: payload.Email
  });

  return UserAxiosConfig.post<
    Api.Account.ResetPasswordByEmail.Response,
    AxiosResponse<Api.Account.ResetPasswordByEmail.Response>,
    ClientApi.Account.ResetPasswordByEmail.Request
  >('/account/resetpasswordbyemail', serializedData, config).then(getApiEndpointResponse);
};

export const changePasswordService = async (
  payload: ClientApi.Account.ChangePassword.Request,
  config?: RequestConfig<ClientApi.Account.ChangePassword.Request>
): Promise<Api.Account.ChangePassword.Response> => {
  const serializedData: Api.Account.ChangePassword.Request = decorateWithAppIdAndKey({
    Email: payload.Email,
    NewPassword: payload.NewPassword,
    Password: payload.Password
  });

  return UserAxiosConfig.post<
    Api.Account.ChangePassword.Response,
    AxiosResponse<Api.Account.ChangePassword.Response>,
    ClientApi.Account.ResetPasswordByEmail.Request
  >('/account/changepassword', serializedData, config).then(getApiEndpointResponse);
};

export const changePasswordFromProfileService = async (
  payload: ClientApi.Account.ChangePasswordFromProfile.Request,
  config?: RequestConfig<ClientApi.Account.ChangePasswordFromProfile.Request>
): Promise<Api.Account.ChangePasswordFromProfile.Response> => {
  const serializedData: Api.Account.ChangePasswordFromProfile.Request = decorateWithAppIdAndKey({
    Email: payload.Email,
    NewPassword: payload.NewPassword,
    Password: payload.Password
  });

  return UserAxiosConfig.post<
    Api.Account.ChangePasswordFromProfile.Response,
    AxiosResponse<Api.Account.ChangePasswordFromProfile.Response>,
    ClientApi.Account.ResetPasswordByEmail.Request
  >('/account/changepasswordonprofile', serializedData, config).then(getApiEndpointResponse);
};

// export const loadChargeRouteService = async (
//   payload: ClientApi.Account.LoadChargeRoute.Request,
//   config?: RequestConfig<ClientApi.Account.LoadChargeRoute.Request>
// ): Promise<Api.Endpoint.Response.SuccessResponse | undefined> => {
//   if (!payload.country) return;

//   return UserAxiosConfig.get<
//     Api.Endpoint.Response.SuccessResponse,
//     AxiosResponse<Api.Endpoint.Response.SuccessResponse>
//   >(`/account/loadchargeroute/${payload.country}`, config).then(getApiEndpointResponse);
// };

export const loadChargeRouteService = async (
  payload: ClientApi.Account.LoadChargeRoute.Request,
  config?: RequestConfig
): Promise<PaymentMethodsI[]> => {
  const token = getState()?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Account.LoadChargeRoute.Response,
    AxiosResponse<ClientApi.Account.LoadChargeRoute.Response>,
    ClientApi.Account.LoadChargeRoute.Request
  >(
    `/api/v${BUSINESS_API_VERSION}/Application/ChargeRoutesByCountry?CountryCode=${payload.country}&PaymentUserType=${payload.paymentUserType}`,
    {
      ...config,
      ...configWithToken
    }
  )
    .then(getApiEndpointResponse)
    .then((res) => {
      const routes = res.data?.routes || [];

      return routes.map((route, index) => ({
        Gateway: route.gateWay,
        Description: route.description,
        Id: index,
        Interval: route.interval
      }));
    });
};

export const getVeriffStatusService = async (
  payload: ClientApi.Account.GetVeriffStatus.Request,
  config?: RequestConfig<ClientApi.Account.GetVeriffStatus.Request>
): Promise<any> => {
  if (!payload.email) return;

  const encodedEmail = encodeURIComponent(payload.email);

  return UserAxiosConfig.get<any, AxiosResponse<any>>(
    `/account/checkveriffbyemail?email=${encodedEmail}`,
    config
  )
    .then(getApiEndpointResponse)
    .then((data) => data.Data);
};

export const veriffSessionsService = async (
  payload: ClientApi.Account.VeriffSessions.Request,
  config?: RequestConfig<ClientApi.Account.ChangePassword.Request>
): Promise<any> => {
  return UserAxiosConfig.post<any, AxiosResponse<any>>('/veriff/sessions', payload.data, config);
};

export const veriffSessionsTestService = async (
  payload: ClientApi.Account.VeriffSessionsTest.Request,
  config?: RequestConfig<ClientApi.Account.ChangePassword.Request>
): Promise<any> => {
  return UserAxiosConfig.get<any, AxiosResponse<any>>(
    `/testemail/testveriff?email=${encodeURIComponent(payload.email)}`,
    config
  );
};

export const getIdIsActiveService = (
  config?: RequestConfig
): Promise<ClientApi.Account.IdIsActive.Response> => {
  const _payload = decorateWithAppIdAndKeyAndEmailAndHash({});

  return UserAxiosConfig.post<
    ClientApi.Account.IdIsActive.Response,
    AxiosResponse<ClientApi.Account.IdIsActive.Response>
  >(`/account/id-is-active`, _payload, config)
    .then(getApiEndpointResponse)
    .then((res) => res);
};

/**
 * ===================
 * STATE RELATED FUNCTIONS
 * ===================
 */

// function used to get logged in user data
export const getLoggedInUserInfo = () => {
  const state = getState();

  const user = (state && state.user) || null;
  const address = (state && state.address) || null;
  const tokens = (state && state.tokens) || null;
  const adminTokens = (state && state.adminTokens) || null;
  const business = (state && state.business) || null;

  return { user, address, tokens, adminTokens, business };
};

// function used to check if a user is logged in
export const checkIfLoggedIn = () => {
  const state = getState();
  return !!(state && state.tokens && state.tokens.hash);
};
